import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  BrowserProvider,
  Contract,
  Interface,
  JsonRpcProvider,
  formatEther,
  formatUnits,
  parseUnits,
  toBigInt,
} from "ethers";

import {
  useAppKit,
  useAppKitAccount,
  useAppKitProvider,
} from "@reown/appkit/react";

import { toast } from "react-toastify";

/* =========================================================
   BSC CONFIG
========================================================= */

const BSC_CHAIN_ID = 56;

const BSC_CHAIN_HEX = "0x38";

const BSC_RPC =
  process.env.REACT_APP_BSC_RPC_URL ||
  "https://bsc-dataseed.bnbchain.org";

/*
  CUSTOM BSC NETWORK

  Used directly with wallet_switchEthereumChain
  and wallet_addEthereumChain.

  So MetaMask / Bitget / BitKeep / Trust Wallet
  do not depend on AppKit switchNetwork().
*/

const BSC_NETWORK = {
  chainId: BSC_CHAIN_HEX,

  chainName: "BNB Smart Chain",

  nativeCurrency: {
    name: "BNB",
    symbol: "BNB",
    decimals: 18,
  },

  rpcUrls: [
    BSC_RPC,
    "https://bsc-dataseed1.binance.org",
    "https://bsc-dataseed2.binance.org",
    "https://bsc-dataseed3.binance.org",
  ],

  blockExplorerUrls: [
    "https://bscscan.com",
  ],
};

/* =========================================================
   CONTRACT ADDRESSES
========================================================= */

const ROUTER_ADDRESS =
  (
    process.env.REACT_APP_PANCAKE_ROUTER ||
    "0x10ed43c718714eb63d5aa57b78b54704e256024e"
  ).toLowerCase();

const WBNB_ADDRESS =
  (
    process.env.REACT_APP_WBNB_ADDRESS ||
    "0xbb4cdb9cbd36b01bd1cbaebf2de08d9173bc095c"
  ).toLowerCase();

const USDT_ADDRESS =
  (
    process.env.REACT_APP_USDT_ADDRESS ||
    "0x55d398326f99059ff775485246999027b3197955"
  ).toLowerCase();

const USDC_ADDRESS =
  (
    process.env.REACT_APP_USDC_ADDRESS ||
    "0x8ac76a51cc950d9822d68b83fe1ad97b32cd580d"
  ).toLowerCase();

const KIND_ADDRESS =
  (
    process.env.REACT_APP_KIND_TOKEN_ADDRESS ||
    "0x41f52a42091a6b2146561bf05b722ad1d0e46f8b"
  ).toLowerCase();

/* =========================================================
   LOGOS
========================================================= */

const BNB_LOGO =
  "/bnb.svg";

const USDT_LOGO =
  "/usdt.png";

const USDC_LOGO =
  "/usdc.svg";

const KIND_LOGO =
  "/kindlogo.png";

/* =========================================================
   CONSTANTS
========================================================= */

const ZERO =
  toBigInt("0");

const BPS_BASE =
  toBigInt("10000");

const DEFAULT_SLIPPAGE =
  "12";

const DEADLINE_MINUTES =
  10;

const TOKEN_SWAP_GAS =
  toBigInt("800000");

const BNB_SWAP_GAS =
  toBigInt("700000");

const APPROVE_GAS =
  toBigInt("150000");

/* =========================================================
   ERC20 ABI
========================================================= */

const ERC20_ABI = [
  "function balanceOf(address account) view returns (uint256)",
  "function allowance(address owner,address spender) view returns (uint256)",
  "function approve(address spender,uint256 amount) returns (bool)",
  "function decimals() view returns (uint8)",
];

/* =========================================================
   PANCAKE V2 ABI
========================================================= */

const ROUTER_ABI = [
  "function getAmountsOut(uint256 amountIn,address[] path) view returns (uint256[] amounts)",

  "function swapExactTokensForTokens(uint256 amountIn,uint256 amountOutMin,address[] path,address to,uint256 deadline) external returns (uint256[] amounts)",

  "function swapExactETHForTokens(uint256 amountOutMin,address[] path,address to,uint256 deadline) external payable returns (uint256[] amounts)",
];

/* =========================================================
   READ PROVIDER
========================================================= */

const readProvider =
  new JsonRpcProvider(
    BSC_RPC,
    {
      chainId: BSC_CHAIN_ID,
      name: "BNB Smart Chain",
    },
    {
      staticNetwork: true,
      batchMaxCount: 1,
    }
  );

/* =========================================================
   TOKENS
========================================================= */

const TOKENS = {
  BNB: {
    symbol: "BNB",
    name: "BNB",
    native: true,
    address: null,
    logo: BNB_LOGO,
  },

  USDT: {
    symbol: "USDT",
    name: "Tether USD",
    native: false,
    address: USDT_ADDRESS,
    logo: USDT_LOGO,
  },

  USDC: {
    symbol: "USDC",
    name: "USD Coin",
    native: false,
    address: USDC_ADDRESS,
    logo: USDC_LOGO,
  },
};

/* =========================================================
   HELPERS
========================================================= */

const safeNumber = (value) => {
  const n =
    Number(value);

  return Number.isFinite(n)
    ? n
    : 0;
};

const fmt = (
  value,
  decimals = 6
) => {
  return safeNumber(value)
    .toLocaleString(
      undefined,
      {
        maximumFractionDigits:
          decimals,
      }
    );
};

const shortAddress = (
  value
) => {
  if (!value) {
    return "";
  }

  return `${value.slice(
    0,
    6
  )}...${value.slice(
    -4
  )}`;
};

/* =========================================================
   CHAIN ID NORMALIZER
========================================================= */

const normalizeChainId = (
  value
) => {
  if (
    value === undefined ||
    value === null
  ) {
    return 0;
  }

  if (
    typeof value ===
    "number"
  ) {
    return value;
  }

  if (
    typeof value ===
    "bigint"
  ) {
    return Number(value);
  }

  const text =
    String(value);

  if (
    text.startsWith(
      "eip155:"
    )
  ) {
    return Number(
      text.split(":")[1]
    );
  }

  if (
    text.startsWith(
      "0x"
    )
  ) {
    return parseInt(
      text,
      16
    );
  }

  return Number(text);
};

/* =========================================================
   ERROR
========================================================= */

const readableError = (
  err
) => {
  console.error(
    "================================"
  );

  console.error(
    "TRANSACTION ERROR"
  );

  console.error(
    "FULL:",
    err
  );

  console.error(
    "CODE:",
    err?.code
  );

  console.error(
    "SHORT:",
    err?.shortMessage
  );

  console.error(
    "REASON:",
    err?.reason
  );

  console.error(
    "DATA:",
    err?.data
  );

  console.error(
    "INFO:",
    err?.info
  );

  console.error(
    "ERROR:",
    err?.error
  );

  console.error(
    "================================"
  );

  const message =
    err?.reason ||
    err?.info?.error?.message ||
    err?.error?.message ||
    err?.data?.message ||
    err?.shortMessage ||
    err?.message ||
    "Transaction failed.";

  const lower =
    String(message)
      .toLowerCase();

  if (
    lower.includes(
      "user rejected"
    ) ||
    lower.includes(
      "user denied"
    ) ||
    lower.includes(
      "rejected the request"
    ) ||
    lower.includes(
      "request rejected"
    )
  ) {
    return "Transaction cancelled.";
  }

  if (
    lower.includes(
      "transfer_from_failed"
    )
  ) {
    return "Token transfer failed. Please approve again.";
  }

  if (
    lower.includes(
      "expired"
    )
  ) {
    return "Swap deadline expired. Please try again.";
  }

  if (
    lower.includes(
      "missing revert data"
    )
  ) {
    return "Transaction reverted without a readable wallet response.";
  }

  return String(message);
};

/* =========================================================
   WALLET ICON
========================================================= */

const WalletIcon = ({
  size = 16,
}) => {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
    >
      <path
        d="M4 7C4 5.34315 5.34315 4 7 4H17C18.6569 4 20 5.34315 20 7V8H7C5.34315 8 4 9.34315 4 11V7Z"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />

      <path
        d="M4 11C4 9.34315 5.34315 8 7 8H20V20H7C5.34315 20 4 18.6569 4 17V11Z"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />

      <path
        d="M15 12H21V16H15C13.8954 16 13 15.1046 13 14C13 12.8954 13.8954 12 15 12Z"
        stroke="currentColor"
        strokeWidth="1.8"
      />

      <circle
        cx="16"
        cy="14"
        r="0.8"
        fill="currentColor"
      />
    </svg>
  );
};

/* =========================================================
   CHAIN BADGE
========================================================= */

const ChainBadge = () => {
  return (
    <div
      className="
        absolute
        -bottom-1
        -right-1
        w-[17px]
        h-[17px]
        rounded-full
        bg-black
        border
        border-[#ffd166]
        flex
        items-center
        justify-center
        overflow-hidden
      "
    >
      <img
        src={BNB_LOGO}
        alt="BNB Chain"
        className="
          w-[13px]
          h-[13px]
          object-contain
        "
      />
    </div>
  );
};

/* =========================================================
   PAGE
========================================================= */

const SwapPage = () => {
  /* =====================================================
     APPKIT
  ===================================================== */

  const {
    open,
  } =
    useAppKit();

  const {
    address,
    isConnected,
  } =
    useAppKitAccount();

  const {
    walletProvider,
  } =
    useAppKitProvider(
      "eip155"
    );

  /* =====================================================
     REFERENCES
  ===================================================== */

  const automaticSwitchDone =
    useRef(false);

  const pendingBuyAfterConnect =
    useRef(false);

  /* =====================================================
     STATE
  ===================================================== */

  const [
    selectedToken,
    setSelectedToken,
  ] =
    useState(
      "BNB"
    );

  const [
    tokenMenu,
    setTokenMenu,
  ] =
    useState(false);

  const [
    amount,
    setAmount,
  ] =
    useState("");

  const [
    slippage,
    setSlippage,
  ] =
    useState(
      DEFAULT_SLIPPAGE
    );

  const [
    editSlippage,
    setEditSlippage,
  ] =
    useState(false);

  const [
    tokenDecimals,
    setTokenDecimals,
  ] =
    useState({
      USDT: 18,
      USDC: 18,
      KIND: 18,
    });

  const [
    rawBalances,
    setRawBalances,
  ] =
    useState({
      BNB: ZERO,
      USDT: ZERO,
      USDC: ZERO,
      KIND: ZERO,
    });

  const [
    balances,
    setBalances,
  ] =
    useState({
      BNB: "0",
      USDT: "0",
      USDC: "0",
      KIND: "0",
    });

  const [
    allowance,
    setAllowance,
  ] =
    useState(
      ZERO
    );

  const [
    quoteRaw,
    setQuoteRaw,
  ] =
    useState(
      ZERO
    );

  const [
    quote,
    setQuote,
  ] =
    useState(
      "0"
    );

  const [
    quoteLoading,
    setQuoteLoading,
  ] =
    useState(false);

  const [
    approving,
    setApproving,
  ] =
    useState(false);

  const [
    swapping,
    setSwapping,
  ] =
    useState(false);

  const [
    switchingNetwork,
    setSwitchingNetwork,
  ] =
    useState(false);

  const [
    walletChainId,
    setWalletChainId,
  ] =
    useState(0);

  /* =====================================================
     CURRENT TOKEN
  ===================================================== */

  const currentToken =
    TOKENS[
      selectedToken
    ];

  const currentDecimals =
    selectedToken ===
    "BNB"
      ? 18
      : tokenDecimals[
          selectedToken
        ] ||
        18;

  const isBsc =
    walletChainId ===
    BSC_CHAIN_ID;

  /* =====================================================
     PATH
  ===================================================== */

  const swapPath =
    useMemo(
      () => {
        if (
          selectedToken ===
          "BNB"
        ) {
          return [
            WBNB_ADDRESS,
            KIND_ADDRESS,
          ];
        }

        if (
          selectedToken ===
          "USDT"
        ) {
          return [
            USDT_ADDRESS,
            WBNB_ADDRESS,
            KIND_ADDRESS,
          ];
        }

        return [
          USDC_ADDRESS,
          WBNB_ADDRESS,
          KIND_ADDRESS,
        ];
      },
      [
        selectedToken,
      ]
    );

  /* =====================================================
     PARSED INPUT
  ===================================================== */

  const parsedInput =
    useMemo(
      () => {
        try {
          if (
            !amount ||
            safeNumber(
              amount
            ) <= 0
          ) {
            return ZERO;
          }

          return parseUnits(
            amount,
            currentDecimals
          );
        } catch {
          return ZERO;
        }
      },
      [
        amount,
        currentDecimals,
      ]
    );

  /* =====================================================
     MINIMUM OUTPUT
  ===================================================== */

  const amountOutMin =
    useMemo(
      () => {
        if (
          quoteRaw <= ZERO
        ) {
          return ZERO;
        }

        let value =
          safeNumber(
            slippage
          );

        if (
          value < 0
        ) {
          value =
            0;
        }

        if (
          value > 99
        ) {
          value =
            99;
        }

        const slippageBps =
          Math.round(
            value *
              100
          );

        const multiplier =
          toBigInt(
            String(
              10000 -
                slippageBps
            )
          );

        return (
          quoteRaw *
          multiplier /
          BPS_BASE
        );
      },
      [
        quoteRaw,
        slippage,
      ]
    );

  const minimumReceived =
    useMemo(
      () => {
        try {
          return formatUnits(
            amountOutMin,
            tokenDecimals.KIND
          );
        } catch {
          return "0";
        }
      },
      [
        amountOutMin,
        tokenDecimals.KIND,
      ]
    );

  /* =====================================================
     APPROVAL STATUS
  ===================================================== */

  const needsApproval =
    useMemo(
      () => {
        if (
          selectedToken ===
          "BNB"
        ) {
          return false;
        }

        if (
          parsedInput <=
          ZERO
        ) {
          return false;
        }

        return (
          allowance <
          parsedInput
        );
      },
      [
        selectedToken,
        allowance,
        parsedInput,
      ]
    );

  /* =====================================================
     GET WALLET CHAIN ID
  ===================================================== */

  const getActualWalletChain =
    useCallback(
      async () => {
        if (
          !walletProvider
        ) {
          return 0;
        }

        try {
          const result =
            await walletProvider.request({
              method:
                "eth_chainId",
            });

          const id =
            normalizeChainId(
              result
            );

          setWalletChainId(
            id
          );

          return id;
        } catch (
          err
        ) {
          console.error(
            "eth_chainId:",
            err
          );

          return 0;
        }
      },
      [
        walletProvider,
      ]
    );

  /* =====================================================
     CUSTOM AUTO BSC SWITCH
  ===================================================== */

  const switchToBsc =
    useCallback(
      async (
        showSuccess = false
      ) => {
        if (
          !walletProvider
        ) {
          return false;
        }

        try {
          setSwitchingNetwork(
            true
          );

          /* =============================================
             CHECK FIRST
          ============================================= */

          const current =
            await getActualWalletChain();

          if (
            current ===
            BSC_CHAIN_ID
          ) {
            automaticSwitchDone.current =
              true;

            return true;
          }

          /* =============================================
             TRY SWITCH
          ============================================= */

          try {
            await walletProvider.request({
              method:
                "wallet_switchEthereumChain",

              params: [
                {
                  chainId:
                    BSC_CHAIN_HEX,
                },
              ],
            });
          } catch (
            switchError
          ) {
            console.error(
              "Initial BSC switch:",
              switchError
            );

            const code =
              switchError?.code ??
              switchError?.error?.code;

            /*
              Wallet may return:
              4902
              -32603
              -32000

              For mobile wallets we attempt add-chain
              if regular switch did not work.
            */

            if (
              code === 4902 ||
              code === -32603 ||
              code === -32000
            ) {
              await walletProvider.request({
                method:
                  "wallet_addEthereumChain",

                params: [
                  BSC_NETWORK,
                ],
              });
            } else {
              throw switchError;
            }
          }

          /* =============================================
             CHECK AGAIN
          ============================================= */

          await new Promise(
            (
              resolve
            ) => {
              setTimeout(
                resolve,
                500
              );
            }
          );

          let after =
            await getActualWalletChain();

          /*
            Some wallets ADD BSC but don't
            automatically switch after adding.
          */

          if (
            after !==
            BSC_CHAIN_ID
          ) {
            await walletProvider.request({
              method:
                "wallet_switchEthereumChain",

              params: [
                {
                  chainId:
                    BSC_CHAIN_HEX,
                },
              ],
            });

            await new Promise(
              (
                resolve
              ) => {
                setTimeout(
                  resolve,
                  500
                );
              }
            );

            after =
              await getActualWalletChain();
          }

          if (
            after !==
            BSC_CHAIN_ID
          ) {
            throw new Error(
              "Wallet did not switch to BNB Smart Chain."
            );
          }

          automaticSwitchDone.current =
            true;

          if (
            showSuccess
          ) {
            toast.success(
              "BNB Smart Chain selected."
            );
          }

          return true;
        } catch (
          err
        ) {
          console.error(
            "AUTO BSC SWITCH ERROR:",
            err
          );

          const text =
            String(
              err?.message ||
                ""
            )
              .toLowerCase();

          if (
            text.includes(
              "rejected"
            ) ||
            text.includes(
              "denied"
            )
          ) {
            toast.error(
              "Network switch cancelled."
            );
          } else {
            toast.error(
              "Please allow BNB Smart Chain in your wallet."
            );
          }

          return false;
        } finally {
          setSwitchingNetwork(
            false
          );
        }
      },
      [
        walletProvider,
        getActualWalletChain,
      ]
    );

  /* =====================================================
     ENSURE BSC
  ===================================================== */

  const ensureBsc =
    useCallback(
      async () => {
        if (
          !walletProvider
        ) {
          return false;
        }

        const chain =
          await getActualWalletChain();

        if (
          chain ===
          BSC_CHAIN_ID
        ) {
          return true;
        }

        return await switchToBsc(
          false
        );
      },
      [
        walletProvider,
        getActualWalletChain,
        switchToBsc,
      ]
    );

  /* =====================================================
     SIGNER
  ===================================================== */

  const getSigner =
    useCallback(
      async () => {
        if (
          !walletProvider
        ) {
          throw new Error(
            "Wallet provider unavailable."
          );
        }

        const provider =
          new BrowserProvider(
            walletProvider
          );

        const signer =
          await provider.getSigner();

        return {
          provider,
          signer,
        };
      },
      [
        walletProvider,
      ]
    );

  /* =====================================================
     GAS PRICE
  ===================================================== */

  const getGasPrice =
    useCallback(
      async () => {
        try {
          const feeData =
            await readProvider
              .getFeeData();

          if (
            feeData?.gasPrice &&
            feeData.gasPrice >
              ZERO
          ) {
            return feeData
              .gasPrice;
          }
        } catch (
          err
        ) {
          console.error(
            "Gas price:",
            err
          );
        }

        return parseUnits(
          "3",
          "gwei"
        );
      },
      []
    );

  /* =====================================================
     DIRECT ROUTER SEND
  ===================================================== */

  const sendRouterTransaction =
    useCallback(
      async ({
        functionName,
        args,
        value = ZERO,
        gasLimit,
      }) => {
        const networkOk =
          await ensureBsc();

        if (
          !networkOk
        ) {
          throw new Error(
            "BNB Smart Chain required."
          );
        }

        const {
          signer,
        } =
          await getSigner();

        const routerInterface =
          new Interface(
            ROUTER_ABI
          );

        const data =
          routerInterface
            .encodeFunctionData(
              functionName,
              args
            );

        const gasPrice =
          await getGasPrice();

        console.log(
          "================================"
        );

        console.log(
          "KIND ROUTER TX"
        );

        console.log(
          "Wallet:",
          address
        );

        console.log(
          "Function:",
          functionName
        );

        console.log(
          "Router:",
          ROUTER_ADDRESS
        );

        console.log(
          "Value:",
          value.toString()
        );

        console.log(
          "Gas:",
          gasLimit.toString()
        );

        console.log(
          "Gas price:",
          gasPrice.toString()
        );

        console.log(
          "================================"
        );

        return await signer
          .sendTransaction({
            to:
              ROUTER_ADDRESS,

            data,

            value,

            gasLimit,

            gasPrice,

            type: 0,
          });
      },
      [
        ensureBsc,
        getSigner,
        getGasPrice,
        address,
      ]
    );

  /* =====================================================
     LOAD DECIMALS
  ===================================================== */

  const loadDecimals =
    useCallback(
      async () => {
        let usdt = 18;
        let usdc = 18;
        let kind = 18;

        try {
          const token =
            new Contract(
              USDT_ADDRESS,
              ERC20_ABI,
              readProvider
            );

          usdt =
            Number(
              await token.decimals()
            );
        } catch (
          err
        ) {
          console.error(
            "USDT decimals:",
            err
          );
        }

        try {
          const token =
            new Contract(
              USDC_ADDRESS,
              ERC20_ABI,
              readProvider
            );

          usdc =
            Number(
              await token.decimals()
            );
        } catch (
          err
        ) {
          console.error(
            "USDC decimals:",
            err
          );
        }

        try {
          const token =
            new Contract(
              KIND_ADDRESS,
              ERC20_ABI,
              readProvider
            );

          kind =
            Number(
              await token.decimals()
            );
        } catch (
          err
        ) {
          console.error(
            "KIND decimals:",
            err
          );
        }

        setTokenDecimals({
          USDT: usdt,
          USDC: usdc,
          KIND: kind,
        });
      },
      []
    );

  /* =====================================================
     LOAD BALANCES

     DISPLAY / MAX ONLY

     NOT USED TO BLOCK SWAP
  ===================================================== */

  const loadBalances =
    useCallback(
      async () => {
        if (
          !address
        ) {
          return;
        }

        let bnb = ZERO;
        let usdt = ZERO;
        let usdc = ZERO;
        let kind = ZERO;

        try {
          bnb =
            await readProvider
              .getBalance(
                address
              );
        } catch (
          err
        ) {
          console.error(
            "BNB balance:",
            err
          );
        }

        try {
          const token =
            new Contract(
              USDT_ADDRESS,
              ERC20_ABI,
              readProvider
            );

          usdt =
            await token
              .balanceOf(
                address
              );
        } catch (
          err
        ) {
          console.error(
            "USDT balance:",
            err
          );
        }

        try {
          const token =
            new Contract(
              USDC_ADDRESS,
              ERC20_ABI,
              readProvider
            );

          usdc =
            await token
              .balanceOf(
                address
              );
        } catch (
          err
        ) {
          console.error(
            "USDC balance:",
            err
          );
        }

        try {
          const token =
            new Contract(
              KIND_ADDRESS,
              ERC20_ABI,
              readProvider
            );

          kind =
            await token
              .balanceOf(
                address
              );
        } catch (
          err
        ) {
          console.error(
            "KIND balance:",
            err
          );
        }

        setRawBalances({
          BNB: bnb,
          USDT: usdt,
          USDC: usdc,
          KIND: kind,
        });

        setBalances({
          BNB:
            formatEther(
              bnb
            ),

          USDT:
            formatUnits(
              usdt,
              tokenDecimals.USDT
            ),

          USDC:
            formatUnits(
              usdc,
              tokenDecimals.USDC
            ),

          KIND:
            formatUnits(
              kind,
              tokenDecimals.KIND
            ),
        });
      },
      [
        address,
        tokenDecimals.USDT,
        tokenDecimals.USDC,
        tokenDecimals.KIND,
      ]
    );

  /* =====================================================
     LOAD ALLOWANCE
  ===================================================== */

  const loadAllowance =
    useCallback(
      async () => {
        if (
          !address ||
          selectedToken ===
            "BNB"
        ) {
          setAllowance(
            ZERO
          );

          return;
        }

        try {
          const token =
            new Contract(
              currentToken.address,
              ERC20_ABI,
              readProvider
            );

          const value =
            await token
              .allowance(
                address,
                ROUTER_ADDRESS
              );

          setAllowance(
            value
          );
        } catch (
          err
        ) {
          console.error(
            "Allowance:",
            err
          );

          setAllowance(
            ZERO
          );
        }
      },
      [
        address,
        selectedToken,
        currentToken.address,
      ]
    );

  /* =====================================================
     LOAD QUOTE
  ===================================================== */

  const loadQuote =
    useCallback(
      async () => {
        if (
          parsedInput <=
          ZERO
        ) {
          setQuoteRaw(
            ZERO
          );

          setQuote(
            "0"
          );

          return;
        }

        try {
          setQuoteLoading(
            true
          );

          const router =
            new Contract(
              ROUTER_ADDRESS,
              ROUTER_ABI,
              readProvider
            );

          const amounts =
            await router
              .getAmountsOut(
                parsedInput,
                swapPath
              );

          if (
            !amounts ||
            amounts.length ===
              0
          ) {
            throw new Error(
              "No quote returned."
            );
          }

          const output =
            amounts[
              amounts.length -
                1
            ];

          setQuoteRaw(
            output
          );

          setQuote(
            formatUnits(
              output,
              tokenDecimals.KIND
            )
          );
        } catch (
          err
        ) {
          console.error(
            "Quote:",
            err
          );

          setQuoteRaw(
            ZERO
          );

          setQuote(
            "0"
          );
        } finally {
          setQuoteLoading(
            false
          );
        }
      },
      [
        parsedInput,
        swapPath,
        tokenDecimals.KIND,
      ]
    );

  /* =====================================================
     MAX
  ===================================================== */

  const handleMax =
    () => {
      const raw =
        rawBalances[
          selectedToken
        ] ||
        ZERO;

      if (
        selectedToken ===
        "BNB"
      ) {
        const reserve =
          parseUnits(
            "0.001",
            18
          );

        if (
          raw <= reserve
        ) {
          setAmount(
            "0"
          );

          return;
        }

        setAmount(
          formatEther(
            raw -
              reserve
          )
        );

        return;
      }

      setAmount(
        formatUnits(
          raw,
          currentDecimals
        )
      );
    };

  /* =====================================================
     APPROVE

     EXACT APPROVAL
  ===================================================== */

  const handleApprove =
    async () => {
      if (
        !isConnected
      ) {
        pendingBuyAfterConnect.current =
          true;

        open({
          view:
            "Connect",
        });

        return;
      }

      const networkOk =
        await ensureBsc();

      if (
        !networkOk
      ) {
        return;
      }

      if (
        selectedToken ===
        "BNB"
      ) {
        return;
      }

      if (
        parsedInput <=
        ZERO
      ) {
        toast.error(
          "Enter an amount."
        );

        return;
      }

      let toastId;

      try {
        setApproving(
          true
        );

        const {
          signer,
        } =
          await getSigner();

        const tokenInterface =
          new Interface(
            ERC20_ABI
          );

        const data =
          tokenInterface
            .encodeFunctionData(
              "approve",
              [
                ROUTER_ADDRESS,
                parsedInput,
              ]
            );

        const gasPrice =
          await getGasPrice();

        const tx =
          await signer
            .sendTransaction({
              to:
                currentToken.address,

              data,

              value:
                ZERO,

              gasLimit:
                APPROVE_GAS,

              gasPrice,

              type: 0,
            });

        toastId =
          toast.loading(
            `Approving ${selectedToken}...`
          );

        await tx.wait();

        toast.update(
          toastId,
          {
            render:
              `${selectedToken} approved successfully!`,

            type:
              "success",

            isLoading:
              false,

            autoClose:
              2500,
          }
        );

        await loadAllowance();
      } catch (
        err
      ) {
        if (
          toastId
        ) {
          toast.dismiss(
            toastId
          );
        }

        toast.error(
          readableError(
            err
          )
        );
      } finally {
        setApproving(
          false
        );
      }
    };

  /* =====================================================
     SWAP
  ===================================================== */

  const executeSwap =
    async () => {
      if (
        !isConnected
      ) {
        pendingBuyAfterConnect.current =
          true;

        open({
          view:
            "Connect",
        });

        return;
      }

      const networkOk =
        await ensureBsc();

      if (
        !networkOk
      ) {
        return;
      }

      if (
        parsedInput <=
        ZERO
      ) {
        toast.error(
          "Enter an amount."
        );

        return;
      }

      if (
        quoteRaw <=
        ZERO
      ) {
        toast.error(
          "No valid KIND quote."
        );

        return;
      }

      if (
        needsApproval
      ) {
        await handleApprove();

        return;
      }

      let toastId;

      try {
        setSwapping(
          true
        );

        const deadline =
          Math.floor(
            Date.now() /
              1000
          ) +
          DEADLINE_MINUTES *
            60;

        console.log(
          "================================"
        );

        console.log(
          "KIND BUY"
        );

        console.log(
          "Wallet:",
          address
        );

        console.log(
          "Token:",
          selectedToken
        );

        console.log(
          "Input:",
          parsedInput.toString()
        );

        console.log(
          "Quote:",
          quoteRaw.toString()
        );

        console.log(
          "Minimum:",
          amountOutMin.toString()
        );

        console.log(
          "Slippage:",
          `${slippage}%`
        );

        console.log(
          "Deadline:",
          deadline
        );

        console.log(
          "Path:",
          swapPath
        );

        console.log(
          "================================"
        );

        let tx;

        /* ===============================================
           BNB -> WBNB -> KIND
        =============================================== */

        if (
          selectedToken ===
          "BNB"
        ) {
          tx =
            await sendRouterTransaction({
              functionName:
                "swapExactETHForTokens",

              args: [
                amountOutMin,

                [
                  WBNB_ADDRESS,
                  KIND_ADDRESS,
                ],

                address,

                deadline,
              ],

              value:
                parsedInput,

              gasLimit:
                BNB_SWAP_GAS,
            });
        }

        /* ===============================================
           USDT / USDC -> WBNB -> KIND
        =============================================== */

        else {
          const path =
            selectedToken ===
            "USDT"
              ? [
                  USDT_ADDRESS,
                  WBNB_ADDRESS,
                  KIND_ADDRESS,
                ]
              : [
                  USDC_ADDRESS,
                  WBNB_ADDRESS,
                  KIND_ADDRESS,
                ];

          tx =
            await sendRouterTransaction({
              functionName:
                "swapExactTokensForTokens",

              args: [
                parsedInput,

                amountOutMin,

                path,

                address,

                deadline,
              ],

              value:
                ZERO,

              gasLimit:
                TOKEN_SWAP_GAS,
            });
        }

        console.log(
          "TX HASH:",
          tx.hash
        );

        toastId =
          toast.loading(
            "Buying KIND..."
          );

        const receipt =
          await tx.wait();

        console.log(
          "TX RECEIPT:",
          receipt
        );

        if (
          receipt?.status !== 1
        ) {
          throw new Error(
            "Swap reverted."
          );
        }

        toast.update(
          toastId,
          {
            render:
              "KIND purchased successfully!",

            type:
              "success",

            isLoading:
              false,

            autoClose:
              3500,
          }
        );

        setAmount("");

        setQuoteRaw(
          ZERO
        );

        setQuote(
          "0"
        );

        await Promise.all([
          loadBalances(),
          loadAllowance(),
        ]);
      } catch (
        err
      ) {
        if (
          toastId
        ) {
          toast.dismiss(
            toastId
          );
        }

        toast.error(
          readableError(
            err
          )
        );
      } finally {
        setSwapping(
          false
        );
      }
    };

  /* =====================================================
     MAIN BUY BUTTON

     DISCONNECTED:
       opens AppKit

     CONNECTED WRONG NETWORK:
       auto switches BSC

     TOKEN NEEDS APPROVAL:
       approve

     READY:
       swap
  ===================================================== */

  const handleBuy =
    async () => {
      if (
        !amount ||
        parsedInput <=
          ZERO
      ) {
        toast.error(
          "Enter an amount."
        );

        return;
      }

      /* =============================================
         CONNECT WALLET
      ============================================= */

      if (
        !isConnected ||
        !walletProvider
      ) {
        pendingBuyAfterConnect.current =
          true;

        open({
          view:
            "Connect",
        });

        return;
      }

      /* =============================================
         AUTO SWITCH BSC
      ============================================= */

      const networkOk =
        await ensureBsc();

      if (
        !networkOk
      ) {
        return;
      }

      /* =============================================
         APPROVAL
      ============================================= */

      if (
        needsApproval
      ) {
        await handleApprove();

        return;
      }

      /* =============================================
         SWAP
      ============================================= */

      await executeSwap();
    };

  /* =====================================================
     BUTTON TEXT

     IMPORTANT:
     NEVER SAYS "CONNECT WALLET"
  ===================================================== */

  const buttonText =
    useMemo(
      () => {
        if (
          switchingNetwork
        ) {
          return "Selecting BNB Chain...";
        }

        if (
          approving
        ) {
          return `Approving ${selectedToken}...`;
        }

        if (
          swapping
        ) {
          return "Buying KIND...";
        }

        if (
          isConnected &&
          needsApproval
        ) {
          return `Approve ${selectedToken}`;
        }

        return "Buy KIND";
      },
      [
        switchingNetwork,
        approving,
        swapping,
        isConnected,
        needsApproval,
        selectedToken,
      ]
    );

  /* =====================================================
     SELECT TOKEN
  ===================================================== */

  const selectToken = (
    symbol
  ) => {
    setSelectedToken(
      symbol
    );

    setTokenMenu(
      false
    );

    setAmount("");

    setQuote(
      "0"
    );

    setQuoteRaw(
      ZERO
    );

    setAllowance(
      ZERO
    );
  };

  /* =====================================================
     INITIAL DECIMALS
  ===================================================== */

  useEffect(
    () => {
      loadDecimals();
    },
    [
      loadDecimals,
    ]
  );

  /* =====================================================
     WALLET CONNECTED:
     AUTO SELECT CUSTOM BSC
  ===================================================== */

  useEffect(
    () => {
      if (
        !isConnected ||
        !walletProvider
      ) {
        automaticSwitchDone.current =
          false;

        setWalletChainId(
          0
        );

        return;
      }

      let cancelled =
        false;

      const run =
        async () => {
          const actualChain =
            await getActualWalletChain();

          if (
            cancelled
          ) {
            return;
          }

          if (
            actualChain ===
            BSC_CHAIN_ID
          ) {
            automaticSwitchDone.current =
              true;

            return;
          }

          /*
            Automatically ask wallet to switch
            immediately after connection.
          */

          if (
            !automaticSwitchDone.current
          ) {
            await switchToBsc(
              false
            );
          }
        };

      /*
        Small delay gives WalletConnect /
        Bitget provider time to become ready.
      */

      const timer =
        setTimeout(
          run,
          300
        );

      return () => {
        cancelled =
          true;

        clearTimeout(
          timer
        );
      };
    },
    [
      isConnected,
      walletProvider,
      getActualWalletChain,
      switchToBsc,
    ]
  );

  /* =====================================================
     CHAIN CHANGED LISTENER

     THIS USES ACTUAL WALLET PROVIDER,
     NOT APPKIT CHAIN STATE.
  ===================================================== */

  useEffect(
    () => {
      if (
        !walletProvider?.on
      ) {
        return undefined;
      }

      const handleChainChanged =
        (
          newChainId
        ) => {
          const normalized =
            normalizeChainId(
              newChainId
            );

          console.log(
            "Wallet chain changed:",
            normalized
          );

          setWalletChainId(
            normalized
          );

          automaticSwitchDone.current =
            normalized ===
            BSC_CHAIN_ID;
        };

      walletProvider.on(
        "chainChanged",
        handleChainChanged
      );

      return () => {
        if (
          walletProvider
            ?.removeListener
        ) {
          walletProvider
            .removeListener(
              "chainChanged",
              handleChainChanged
            );
        }
      };
    },
    [
      walletProvider,
    ]
  );

  /* =====================================================
     LOAD USER DATA
  ===================================================== */

  useEffect(
    () => {
      if (
        !isConnected ||
        !address
      ) {
        return undefined;
      }

      loadBalances();

      loadAllowance();

      const interval =
        setInterval(
          () => {
            loadBalances();

            loadAllowance();
          },
          15000
        );

      return () =>
        clearInterval(
          interval
        );
    },
    [
      isConnected,
      address,
      selectedToken,
      loadBalances,
      loadAllowance,
    ]
  );

  /* =====================================================
     QUOTE
  ===================================================== */

  useEffect(
    () => {
      const timer =
        setTimeout(
          () => {
            loadQuote();
          },
          400
        );

      return () =>
        clearTimeout(
          timer
        );
    },
    [
      loadQuote,
    ]
  );

  /* =====================================================
     UI
  ===================================================== */

  return (
    <div
      className="
        min-h-screen
        px-3
        py-8
        text-white
        flex
        justify-center
      "
      style={{
        background:
          "#050505",
      }}
    >
      <div
        className="
          w-full
          max-w-[500px]
        "
      >
        <div
          className="
            rounded-[26px]
            border
            border-[#463a17]
            overflow-visible
            shadow-2xl
          "
          style={{
            background:
              "#111111",
          }}
        >
          {/* =============================================
              FROM
          ============================================= */}

          <div
            className="
              p-4
              sm:p-5
              pb-10
            "
          >
            <div
              className="
                flex
                justify-between
                items-center
                mb-4
              "
            >
              <div
                className="
                  flex
                  items-center
                  gap-2
                  text-[11px]
                  text-gray-400
                "
              >
                <span>
                  From:
                </span>

                <span
                  className="
                    text-[#ffd166]
                  "
                >
                  <WalletIcon
                    size={
                      16
                    }
                  />
                </span>

                <span
                  className="
                    text-gray-300
                    font-semibold
                  "
                >
                  {isConnected
                    ? shortAddress(
                        address
                      )
                    : "Wallet"}
                </span>
              </div>

              <button
                type="button"
                onClick={
                  handleMax
                }
                className="
                  flex
                  items-center
                  gap-1.5
                  text-[11px]
                  text-[#ffd166]
                "
              >
                <WalletIcon
                  size={
                    14
                  }
                />

                {fmt(
                  balances[
                    selectedToken
                  ],
                  6
                )}
              </button>
            </div>

            {/* =============================================
                INPUT BOX
            ============================================= */}

            <div
              className="
                rounded-[22px]
                p-5
                flex
                items-center
                justify-between
                gap-4
                border
                border-[#40330d]
              "
              style={{
                background:
                  "linear-gradient(135deg,#191919,#101010)",
              }}
            >
              {/* ===========================================
                  TOKEN
              =========================================== */}

              <div
                className="
                  relative
                  shrink-0
                "
              >
                <button
                  type="button"
                  onClick={() =>
                    setTokenMenu(
                      (
                        value
                      ) =>
                        !value
                    )
                  }
                  className="
                    flex
                    items-center
                    gap-3
                  "
                >
                  <div
                    className="
                      relative
                    "
                  >
                    <img
                      src={
                        currentToken.logo
                      }
                      alt={
                        selectedToken
                      }
                      className="
                        w-11
                        h-11
                        rounded-full
                        object-contain
                        bg-black
                      "
                    />

                    <ChainBadge />
                  </div>

                  <div
                    className="
                      text-left
                    "
                  >
                    <div
                      className="
                        flex
                        items-center
                        gap-2
                        text-xl
                        font-bold
                      "
                    >
                      {
                        selectedToken
                      }

                      <span
                        className="
                          text-[#ffd166]
                        "
                      >
                        ▼
                      </span>
                    </div>

                    <div
                      className="
                        text-xs
                        text-gray-500
                      "
                    >
                      BNB Chain
                    </div>
                  </div>
                </button>

                {/* =========================================
                    TOKEN MENU
                ========================================= */}

                {tokenMenu && (
                  <div
                    className="
                      absolute
                      left-0
                      top-[64px]
                      z-50
                      w-[220px]
                      rounded-[18px]
                      border
                      border-[#493a10]
                      overflow-hidden
                      shadow-2xl
                    "
                    style={{
                      background:
                        "#080808",
                    }}
                  >
                    {Object.keys(
                      TOKENS
                    ).map(
                      (
                        symbol
                      ) => {
                        const token =
                          TOKENS[
                            symbol
                          ];

                        return (
                          <button
                            key={
                              symbol
                            }
                            type="button"
                            onClick={() =>
                              selectToken(
                                symbol
                              )
                            }
                            className="
                              w-full
                              px-4
                              py-3
                              flex
                              items-center
                              justify-between
                              border-b
                              border-[#29230f]
                              last:border-0
                              hover:bg-[#ffd16610]
                            "
                          >
                            <div
                              className="
                                flex
                                items-center
                                gap-3
                              "
                            >
                              <div
                                className="
                                  relative
                                "
                              >
                                <img
                                  src={
                                    token.logo
                                  }
                                  alt={
                                    symbol
                                  }
                                  className="
                                    w-9
                                    h-9
                                    rounded-full
                                    object-contain
                                  "
                                />

                                <ChainBadge />
                              </div>

                              <div
                                className="
                                  text-left
                                "
                              >
                                <div
                                  className="
                                    font-semibold
                                  "
                                >
                                  {
                                    symbol
                                  }
                                </div>

                                <div
                                  className="
                                    text-[10px]
                                    text-gray-500
                                  "
                                >
                                  {
                                    token.name
                                  }
                                </div>
                              </div>
                            </div>

                            {selectedToken ===
                              symbol && (
                              <span
                                className="
                                  text-[#ffd166]
                                "
                              >
                                ✓
                              </span>
                            )}
                          </button>
                        );
                      }
                    )}
                  </div>
                )}
              </div>

              {/* ===========================================
                  INPUT
              =========================================== */}

              <div
                className="
                  flex-1
                  min-w-0
                  text-right
                "
              >
                <input
                  type="text"
                  inputMode="decimal"
                  value={
                    amount
                  }
                  onChange={(
                    event
                  ) => {
                    const value =
                      event.target
                        .value;

                    if (
                      value ===
                        "" ||
                      /^\d*\.?\d*$/.test(
                        value
                      )
                    ) {
                      setAmount(
                        value
                      );
                    }
                  }}
                  placeholder="0.00"
                  className="
                    w-full
                    bg-transparent
                    outline-none
                    text-right
                    text-3xl
                    sm:text-4xl
                    font-bold
                    text-white
                    placeholder:text-[#555]
                  "
                />

                <button
                  type="button"
                  onClick={
                    handleMax
                  }
                  className="
                    mt-1
                    text-[11px]
                    text-gray-500
                  "
                >
                  Balance{" "}

                  <span
                    className="
                      text-[#ffd166]
                    "
                  >
                    {fmt(
                      balances[
                        selectedToken
                      ],
                      6
                    )}
                  </span>
                </button>
              </div>
            </div>
          </div>

          {/* =============================================
              CENTER
          ============================================= */}

          <div
            className="
              relative
              h-[1px]
              bg-[#2d2d2d]
            "
          >
            <div
              className="
                absolute
                left-1/2
                top-1/2
                -translate-x-1/2
                -translate-y-1/2
                w-12
                h-12
                rounded-full
                border
                border-[#4a3a0d]
                bg-[#090909]
                flex
                items-center
                justify-center
              "
            >
              <span
                className="
                  text-[#ffd166]
                  text-xl
                "
              >
                ↓
              </span>
            </div>
          </div>

          {/* =============================================
              TO
          ============================================= */}

          <div
            className="
              p-4
              sm:p-5
              pt-10
            "
          >
            <div
              className="
                flex
                justify-between
                items-center
                mb-4
              "
            >
              <div
                className="
                  flex
                  items-center
                  gap-2
                  text-[11px]
                  text-gray-400
                "
              >
                <span>
                  To:
                </span>

                <span
                  className="
                    text-[#ffd166]
                  "
                >
                  <WalletIcon
                    size={
                      16
                    }
                  />
                </span>

                <span
                  className="
                    text-gray-300
                    font-semibold
                  "
                >
                  {isConnected
                    ? shortAddress(
                        address
                      )
                    : "Wallet"}
                </span>
              </div>

              <div
                className="
                  flex
                  items-center
                  gap-1.5
                  text-[11px]
                  text-[#ffd166]
                "
              >
                <WalletIcon
                  size={
                    14
                  }
                />

                {fmt(
                  balances.KIND,
                  4
                )}
              </div>
            </div>

            {/* =============================================
                KIND OUTPUT
            ============================================= */}

            <div
              className="
                rounded-[22px]
                p-5
                flex
                items-center
                justify-between
                gap-4
                border
                border-[#40330d]
              "
              style={{
                background:
                  "linear-gradient(135deg,#191919,#101010)",
              }}
            >
              <div
                className="
                  flex
                  items-center
                  gap-3
                  shrink-0
                "
              >
                <div
                  className="
                    relative
                  "
                >
                  <img
                    src={
                      KIND_LOGO
                    }
                    alt="KIND"
                    className="
                      w-11
                      h-11
                      rounded-full
                      object-contain
                    "
                  />

                  <ChainBadge />
                </div>

                <div>
                  <div
                    className="
                      text-xl
                      font-bold
                    "
                  >
                    KIND
                  </div>

                  <div
                    className="
                      text-xs
                      text-gray-500
                    "
                  >
                    BNB Chain
                  </div>
                </div>
              </div>

              <div
                className="
                  flex-1
                  min-w-0
                  text-right
                "
              >
                <div
                  className="
                    text-3xl
                    sm:text-4xl
                    font-bold
                    truncate
                  "
                >
                  {quoteLoading
                    ? "..."
                    : fmt(
                        quote,
                        6
                      )}
                </div>

                <div
                  className="
                    mt-1
                    text-[11px]
                    text-gray-500
                  "
                >
                  Balance{" "}

                  <span
                    className="
                      text-[#ffd166]
                    "
                  >
                    {fmt(
                      balances.KIND,
                      4
                    )}
                  </span>
                </div>
              </div>
            </div>

            {/* =============================================
                DETAILS
            ============================================= */}

            {quoteRaw >
              ZERO && (
              <div
                className="
                  mt-4
                  space-y-2
                  text-[11px]
                "
              >
          

             


              </div>
            )}
          </div>

          {/* =============================================
              SLIPPAGE
          ============================================= */}

          <div
            className="
              mx-4
              sm:mx-5
              mb-4
              pt-4
              border-t
              border-[#292929]
            "
          >
            <div
              className="
                flex
                justify-between
                items-center
              "
            >
              <span
                className="
                  text-xs
                  text-gray-400
                "
              >
                Slippage Tolerance
              </span>

              {!editSlippage ? (
                <button
                  type="button"
                  onClick={() =>
                    setEditSlippage(
                      true
                    )
                  }
                  className="
                    px-3
                    py-2
                    rounded-xl
                    border
                    border-[#49380b]
                    bg-[#191507]
                    text-sm
                    font-bold
                    text-[#ffd166]
                  "
                >
                  {safeNumber(
                    slippage
                  ).toFixed(
                    2
                  )}
                  % ✎
                </button>
              ) : (
                <div
                  className="
                    flex
                    items-center
                    gap-2
                  "
                >
                  <input
                    type="text"
                    inputMode="decimal"
                    value={
                      slippage
                    }
                    onChange={(
                      event
                    ) => {
                      const value =
                        event.target
                          .value;

                      if (
                        value ===
                          "" ||
                        /^\d*\.?\d*$/.test(
                          value
                        )
                      ) {
                        setSlippage(
                          value
                        );
                      }
                    }}
                    className="
                      w-20
                      bg-black
                      border
                      border-[#ffd16655]
                      rounded-xl
                      px-3
                      py-2
                      text-right
                      outline-none
                      text-[#ffd166]
                    "
                  />

                  <span
                    className="
                      text-[#ffd166]
                    "
                  >
                    %
                  </span>

                  <button
                    type="button"
                    onClick={() =>
                      setEditSlippage(
                        false
                      )
                    }
                    className="
                      text-xs
                      text-gray-400
                    "
                  >
                    Done
                  </button>
                </div>
              )}
            </div>

            {safeNumber(
              slippage
            ) >
              20 && (
              <div
                className="
                  mt-3
                  rounded-xl
                  border
                  border-[#5a4210]
                  bg-[#211907]
                  px-3
                  py-2
                  text-[11px]
                  text-[#ffd166]
                "
              >
                ⚠ High slippage. Proceed with caution.
              </div>
            )}
          </div>

          {/* =============================================
              BUY BUTTON
          ============================================= */}

          <div
            className="
              px-4
              sm:px-5
              pb-5
            "
          >
            <button
              type="button"
              onClick={
                handleBuy
              }
              disabled={
                approving ||
                swapping ||
                switchingNetwork
              }
              className="
                w-full
                rounded-[16px]
                py-4
                text-base
                font-bold
                transition-all
                hover:brightness-110
                active:scale-[0.99]
                disabled:opacity-60
                disabled:cursor-not-allowed
              "
              style={{
                background:
                  (
                    !amount ||
                    safeNumber(
                      amount
                    ) <= 0
                  )
                    ? "#272727"
                    : "linear-gradient(90deg,#ffd166 0%,#ffb52d 50%,#ff9f1c 100%)",

                color:
                  (
                    !amount ||
                    safeNumber(
                      amount
                    ) <= 0
                  )
                    ? "#777"
                    : "#050505",
              }}
            >
              {
                buttonText
              }
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SwapPage;